'use client'
import { createContext, use, type ReactNode, useMemo } from 'react'
import { type QueryObserverBaseResult } from '@tanstack/react-query'
import { type AristotleExperiments } from '@shared/types/experiments'
import { useQueryExperiments } from '@/features/shared/services/Experiments/queries'
import { defaultExperiments } from '@/features/shared/services/Experiments/defaultExperiments'

export type ExperimentsContextType = Pick<
  QueryObserverBaseResult<AristotleExperiments>,
  'status' | 'data'
>

export const ExperimentsContext = createContext<ExperimentsContextType | null>(null)

export const ExperimentsContextProvider = ({
  enabled,
  children,
}: {
  enabled?: boolean
  children: ReactNode
}) => {
  const { data, status } = useQueryExperiments({ enabled })
  const value = useMemo(() => {
    return {
      data: data || defaultExperiments,
      status,
    }
  }, [data, status])

  return <ExperimentsContext value={value}>{children}</ExperimentsContext>
}

export const useExperimentsContext = () => {
  const context = use(ExperimentsContext)
  if (!context) {
    throw new Error('useExperimentsContext must be used within an ExperimentsContextProvider')
  }
  return context
}
